import React, { useMemo, useRef } from "react";
import {
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Box,
  TextField,
  ListSubheader,
  InputAdornment,
} from "@mui/material";
import { Field, useFormikContext } from "formik";
import PropTypes from "prop-types";
import FormikLabel from "./FormLabel";
import CancelIcon from "@mui/icons-material/Cancel";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";

const DropdownFieldComponent = ({
  field,
  errors,
  touched,
  handleFieldChange,
  dropdownOptions,
}) => {
  const { setFieldValue } = useFormikContext();
  const [menuPlacement, setMenuPlacement] = useState("bottom");
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  // ----
  const [searchTerm, setSearchTerm] = useState("");

  const isMultiple = field.multiple;
  const options = dropdownOptions[field.fieldId] || [];

  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      opt.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, options]);
  // ----

  const handleOpen = () => {
    const rect = selectRef.current?.getBoundingClientRect?.();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 200 && spaceAbove > spaceBelow) {
      setMenuPlacement("top");
    } else {
      setMenuPlacement("bottom");
    }
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  return (
    <FormControl
      fullWidth
      error={touched[field.fieldId] && !!errors[field.fieldId]}
      ref={selectRef}
    >
      <FormikLabel text={field.fieldName} required={field.required} />
      <Field name={field.fieldId}>
        {({ field: formikField }) => {
          const isMultiple = field.multiple;
          const options = dropdownOptions[field.fieldId] || [];

          return (
            <Select
              inputRef={selectRef}
              onOpen={handleOpen}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 190,
                    zIndex: 1300,
                  },
                },
                anchorOrigin: {
                  vertical: menuPlacement,
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: menuPlacement === "bottom" ? "top" : "bottom",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              hidden={field.show}
              disabled={field.disabled}
              multiple={isMultiple}
              displayEmpty
              value={formikField.value || (isMultiple ? [] : "")}
              onChange={(event) => {
                let selectedValues = event.target.value;

                // Store only values, not names
                const formattedValues = isMultiple
                  ? Array.isArray(selectedValues)
                    ? selectedValues
                    : [selectedValues]
                  : selectedValues;

                setFieldValue(field.fieldId, formattedValues);
                handleFieldChange(event, field.fieldId);
              }}
              renderValue={(selected) => {
                if (
                  !selected ||
                  (Array.isArray(selected) && selected.length === 0)
                ) {
                  return field.placeholder;
                }

                return isMultiple ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      maxHeight: "50px",
                      overflowY: "auto",
                    }}
                  >
                    {selected.map((val) => {
                      // changed
                      const selectedOption = options.find(
                        (opt) => opt.value === val
                      );
                      return (
                        <Chip
                          key={val}
                          label={selectedOption?.name || val}
                          sx={{ margin: "2px", height: "20px" }}
                          onDelete={() => {
                            const newValue = selected.filter((v) => v !== val);
                            setFieldValue(field.fieldId, newValue);
                          }}
                          deleteIcon={
                            <CancelIcon
                              onMouseDown={(event) => event.stopPropagation()}
                            />
                          }
                        />
                      );
                    })}
                  </Box>
                ) : (
                  options.find((opt) => opt.value === selected)?.name ||
                    field.placeholder
                );
              }}
              sx={{
                "& .MuiSelect-select": {
                  padding: "8px",
                  borderRadius: "8px",
                  backgroundColor: field.disabled ? "#f5f5f5" : "inherit",
                  cursor:
                    field.disabled || field.readOnly
                      ? "not-allowed"
                      : "inherit",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  overflowY: "auto",
                  maxHeight: "50px",
                },
                "&.Mui-disabled .MuiSelect-select": {
                  cursor: "not-allowed",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                  borderColor: "#c2bfbd",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#c2bfbd",
                },
              }}
            >
              <MenuItem
                disableRipple
                disableTouchRipple
                sx={{
                  background: "white !important",
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </MenuItem>
              <MenuItem value="" disabled>
                {field.placeholder || field.fieldName}
              </MenuItem>

              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ListItemText primary={option.name} />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No results found</MenuItem>
              )}
            </Select>
          );
        }}
      </Field>
      <FormHelperText>
        {touched[field.fieldId] && errors[field.fieldId]}
      </FormHelperText>
    </FormControl>
  );
};

DropdownFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.object.isRequired,
};

export default DropdownFieldComponent;
